import React, { useEffect, useState } from 'react'
import { Tabs, Tab, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../auth/auth-context'
import LinkScriptDashboard from './link-script-dashboard'
import LinkScriptAnalysis from './link-script-analysis'
import LinkScriptSettings from './link-script-settings/link-script-settings'
import LinkScriptPartnerships from './partnerships/link-script-partnerships'
import PartnershipDetail from './partnerships/partnership-detail'
import PartnershipPrint from './partnerships/partnership-print'
import { IPublisher } from './types'
import useFetch from '../../../_fetch'

const LinkScriptPage: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const hasPartnershipsAccess = user?.kind === 'publisher' && !!user?.byClick?.enabled && !!user?.partnerships?.enabled

    useEffect(() => {
        if (!hasPartnershipsAccess && location.pathname.startsWith('/link-script/partnerships')) {
            navigate('/link-script', { replace: true })
        }
    }, [hasPartnershipsAccess, location.pathname, navigate])

    const [publishers, setPublishers] = useState<IPublisher[]>([])
    const [loading, setLoading] = useState<boolean>(true) // ny state til loading
    const isPartnershipPrintRoute = /^\/link-script\/partnerships\/[^/]+\/print$/.test(location.pathname)

    useEffect(() => {
        if (isPartnershipPrintRoute) {
            setLoading(false)
            return
        }

        const fetchPublishers = async () => {
            try {
                const resPub = await doFetch('/publisher-campaigns/all')
                if (resPub.status >= 400) throw await resPub.text()

                const data = await resPub.json()
                setPublishers(data)
            } catch (err) {
                console.error('Failed to fetch publishers:', err)
            } finally {
                setLoading(false) // stop spinner når fetch er færdig
            }
        }
        fetchPublishers()
    }, [doFetch, isPartnershipPrintRoute])

    const activeKey =
        location.pathname === '/link-script/settings'
            ? 'settings'
            : location.pathname === '/link-script/analysis'
              ? 'analysis'
              : 'overview'

    const handleSelect = (key: string | null) => {
        if (key === 'overview') navigate('/link-script')
        if (key === 'analysis') navigate('/link-script/analysis')
        if (key === 'settings') navigate('/link-script/settings')
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    if (hasPartnershipsAccess && isPartnershipPrintRoute) {
        return <PartnershipPrint />
    }

    if (hasPartnershipsAccess && location.pathname.startsWith('/link-script/partnerships')) {
        return location.pathname === '/link-script/partnerships'
            ? <LinkScriptPartnerships />
            : <PartnershipDetail />
    }

    return (
        <div>
            <Tabs activeKey={activeKey} onSelect={handleSelect} className="mb-3">
                <Tab eventKey="overview" title="Overview" />
                <Tab eventKey="analysis" title="Link Performance" />
                <Tab eventKey="settings" title="Settings" />
            </Tabs>

            {activeKey === 'overview' && <LinkScriptDashboard publishers={publishers} />}
            {activeKey === 'analysis' && <LinkScriptAnalysis />}
            {activeKey === 'settings' && <LinkScriptSettings publishers={publishers} />}
        </div>
    )
}

export default LinkScriptPage
